import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Factory, CheckCircle, ArrowRight, TrendingUp, Shield, Clock } from 'lucide-react';

const Manufacturing = () => {
  const challenges = [
    'Complex production scheduling',
    'Quality control and compliance',
    'Inventory management across multiple locations',
    'Equipment maintenance tracking',
    'Real-time production visibility'
  ];

  const solutions = [
    {
      icon: <Factory className="w-8 h-8" />,
      title: 'Production Tracking',
      description: 'Monitor every stage of your manufacturing process in real-time'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Quality Assurance',
      description: 'Automated quality checks and compliance reporting'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Asset Management',
      description: 'Track equipment, tools, and materials across your facility'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Predictive Maintenance',
      description: 'IoT-enabled monitoring to prevent equipment downtime'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-center mb-6">
            <Factory className="w-16 h-16 text-blue-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">Manufacturing Solutions</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto text-center">
            Optimize production, reduce costs, and improve quality with our comprehensive manufacturing solutions
          </p>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Manufacturing Challenges We Solve</h2>
            <p className="text-lg text-slate-600">Common pain points in modern manufacturing operations</p>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-3">
            {challenges.map((challenge, index) => (
              <div key={index} className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <span className="text-slate-700 text-lg">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Manufacturing Solutions</h2>
            <p className="text-lg text-slate-600">Tailored technology for manufacturing excellence</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {solutions.map((solution, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
                      {solution.icon}
                    </div>
                    <CardTitle className="text-xl">{solution.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{solution.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-600 to-cyan-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Manufacturing?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Let's discuss how our solutions can optimize your production operations
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100 px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
              Schedule a Consultation <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Manufacturing;